/**
 * Artifact reviews store — document-level review workflow.
 *
 * Owns the per-artifact review history and the active pending review
 * (if any). Submission, decision, and cancel actions update the cached
 * BidArtifact via the artifacts store.
 */

import { create } from 'zustand'
import {
  submitArtifactForReview,
  decideArtifactReview,
  listArtifactReviews,
  cancelArtifactReview,
  type ArtifactReview,
  type ArtifactReviewDecision,
  type BidArtifact,
} from '@/lib/api'

export interface ArtifactReviewsState {
  byArtifactId: Record<string, ArtifactReview[]>
  loading: Record<string, boolean>
  submitting: Record<string, boolean>
  deciding: Record<string, boolean>
  error: string | null

  load: (artifactId: string) => Promise<ArtifactReview[]>
  submit: (
    artifactId: string,
    reviewerId: string,
  ) => Promise<{ review: ArtifactReview; artifact: BidArtifact }>
  decide: (
    artifactId: string,
    reviewId: string,
    decision: ArtifactReviewDecision,
    overallNotes?: string | null,
  ) => Promise<{ review: ArtifactReview; artifact: BidArtifact; sectionsFlipped: number }>
  cancel: (
    artifactId: string,
  ) => Promise<{ review: ArtifactReview; artifact: BidArtifact }>

  /** Pending review for an artifact (status === 'pending') or null. */
  currentPending: (artifactId: string) => ArtifactReview | null

  clearError: () => void
  reset: () => void
}

function setOne<T>(record: Record<string, T>, key: string, value: T): Record<string, T> {
  return { ...record, [key]: value }
}

function unshiftOrReplace(list: ArtifactReview[], review: ArtifactReview): ArtifactReview[] {
  const idx = list.findIndex((r) => r.id === review.id)
  if (idx === -1) return [review, ...list]
  const next = list.slice()
  next[idx] = review
  return next
}

export const useArtifactReviewsStore = create<ArtifactReviewsState>()((set, get) => ({
  byArtifactId: {},
  loading: {},
  submitting: {},
  deciding: {},
  error: null,

  load: async (artifactId) => {
    set((s) => ({ loading: setOne(s.loading, artifactId, true), error: null }))
    try {
      const res = await listArtifactReviews(artifactId)
      set((s) => ({
        byArtifactId: setOne(s.byArtifactId, artifactId, res.reviews),
        loading: setOne(s.loading, artifactId, false),
      }))
      return res.reviews
    } catch (err) {
      set((s) => ({
        loading: setOne(s.loading, artifactId, false),
        error: err instanceof Error ? err.message : 'Failed to load reviews',
      }))
      throw err
    }
  },

  submit: async (artifactId, reviewerId) => {
    set((s) => ({ submitting: setOne(s.submitting, artifactId, true), error: null }))
    try {
      const res = await submitArtifactForReview(artifactId, reviewerId)
      // Optimistically prepend the new review (will be re-fetched on next load if needed).
      set((s) => ({
        byArtifactId: setOne(
          s.byArtifactId,
          artifactId,
          unshiftOrReplace(s.byArtifactId[artifactId] ?? [], { ...res.review, reviewerEmail: null, reviewerFullName: null, reviewerAvatarUrl: null, requestedByEmail: null, requestedByFullName: null } as ArtifactReview),
        ),
        submitting: setOne(s.submitting, artifactId, false),
      }))
      return res
    } catch (err) {
      set((s) => ({
        submitting: setOne(s.submitting, artifactId, false),
        error: err instanceof Error ? err.message : 'Failed to submit for review',
      }))
      throw err
    }
  },

  decide: async (artifactId, reviewId, decision, overallNotes) => {
    set((s) => ({ deciding: setOne(s.deciding, artifactId, true), error: null }))
    try {
      const res = await decideArtifactReview(artifactId, reviewId, decision, overallNotes)
      set((s) => ({
        byArtifactId: setOne(
          s.byArtifactId,
          artifactId,
          unshiftOrReplace(s.byArtifactId[artifactId] ?? [], res.review),
        ),
        deciding: setOne(s.deciding, artifactId, false),
      }))
      return res
    } catch (err) {
      set((s) => ({
        deciding: setOne(s.deciding, artifactId, false),
        error: err instanceof Error ? err.message : 'Failed to record decision',
      }))
      throw err
    }
  },

  cancel: async (artifactId) => {
    set((s) => ({ deciding: setOne(s.deciding, artifactId, true), error: null }))
    try {
      const res = await cancelArtifactReview(artifactId)
      set((s) => ({
        byArtifactId: setOne(
          s.byArtifactId,
          artifactId,
          unshiftOrReplace(s.byArtifactId[artifactId] ?? [], res.review),
        ),
        deciding: setOne(s.deciding, artifactId, false),
      }))
      return res
    } catch (err) {
      set((s) => ({
        deciding: setOne(s.deciding, artifactId, false),
        error: err instanceof Error ? err.message : 'Failed to cancel review',
      }))
      throw err
    }
  },

  currentPending: (artifactId) => {
    const list = get().byArtifactId[artifactId] ?? []
    return list.find((r) => r.status === 'pending') ?? null
  },

  clearError: () => set({ error: null }),
  reset: () => set({ byArtifactId: {}, loading: {}, submitting: {}, deciding: {}, error: null }),
}))
